import { useEffect, useRef, useState } from "react";
import type { AutosaveResponse } from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, useWork } from "../lib/api";
import { publishPendingGifts } from "../lib/gifts";
import {
  checkHummingFile,
  HUMMING_FILE_ACCEPT,
  HUMMING_FILE_HINT,
  HUMMING_MAX_SECONDS,
  HUMMING_MIN_SECONDS,
  HUMMING_TOO_SHORT_MESSAGE,
} from "../lib/hummingFile";
import { navigate } from "../lib/navigation";
import { pathForStep } from "../lib/session";

export function HummingPage({ workId }: { workId: string }) {
  const query = useWork(workId);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  // 소리가 «녹음»에서 왔는지 «파일»에서 왔는지 — 안내 문구가 갈린다.
  // 파일을 골라 놓고 「다시 녹음해 주세요」라고 하면 학생은 무엇을 하라는 건지 알 수 없다.
  const [source, setSource] = useState<"recording" | "file">("recording");
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [lyrics, setLyrics] = useState("");
  const stoppedAtRef = useRef<number | null>(null);
  // 🔑 「다시 녹음하기」로 돌아오면 저장된 가사를 이어 받는다 — 빈칸으로 두면 다음 제출이 가사를 지운다.
  const lyricsLoaded = useRef(false);
  const savedLyrics = query.data?.work.mode === "humming" ? query.data.work.lyrics : null;
  useEffect(() => {
    if (lyricsLoaded.current || !query.data) return;
    lyricsLoaded.current = true;
    if (savedLyrics) setLyrics(savedLyrics);
  }, [query.data, savedLyrics]);
  /**
   * 마이크로 실제로 들어오는 소리의 세기(막대 12개, 0~1).
   *
   * 🚨 종전에는 이 자리가 **CSS 애니메이션**이었다 — 높이가 코드에 박힌 막대가 입력과 무관하게
   *    늘 흔들렸다(2026-08-13 jery: 「보여주기식 애니메이션」). 그 파형은 「마이크가 잡히고
   *    있는가」라는, 학생이 이 화면에서 가장 알고 싶은 것을 **거짓으로** 답한다 —
   *    음소거된 마이크로 30초를 부르고도 잘 되고 있다고 믿게 된다.
   *
   * 🔑 이제 AnalyserNode 로 실제 주파수 세기를 읽어 그린다. 조용하면 막대가 바닥에서 멈춘다.
   */
  const [levels, setLevels] = useState<number[]>(() => new Array(12).fill(0));
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserFrameRef = useRef<number | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [limitNotice, setLimitNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!recording) return undefined;
    const timer = window.setInterval(() => {
      const elapsed = Math.min(60, Math.floor((Date.now() - startedAtRef.current) / 1_000));
      setSeconds(elapsed);
      if (elapsed >= 60 && recorderRef.current?.state === "recording") {
        setLimitNotice("60초까지 녹음할 수 있어요");
        recorderRef.current.stop();
      }
    }, 200);
    return () => window.clearInterval(timer);
  }, [recording]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (analyserFrameRef.current !== null) window.cancelAnimationFrame(analyserFrameRef.current);
    void audioContextRef.current?.close().catch(() => undefined);
    if (recordingUrl) URL.revokeObjectURL(recordingUrl);
  }, [recordingUrl]);

  if (query.loading) return <LoadingPage label="허밍 준비 중..." />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "허밍 화면을 불러오지 못했습니다."} />;
  }

  /** 마이크 분석을 걷어낸다 — 녹음이 끝나거나 화면을 떠날 때 반드시 부른다. */
  const stopLevelMeter = (): void => {
    if (analyserFrameRef.current !== null) {
      window.cancelAnimationFrame(analyserFrameRef.current);
      analyserFrameRef.current = null;
    }
    void audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
    setLevels(new Array(12).fill(0));
  };

  /**
   * 들어오는 소리를 막대 12개의 세기로 옮긴다.
   *
   * 🔑 주파수 스펙트럼을 12칸으로 나눠 각 칸의 평균을 쓴다. 사람이 «파형» 이라고 부를 때
   *    기대하는 «소리를 내면 움직이고 멈추면 선다» 가 이것으로 충족된다.
   */
  const startLevelMeter = (stream: MediaStream): void => {
    const AudioContextClass = window.AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    // 🚨 웹오디오를 못 쓰는 기기에서도 **녹음 자체는 계속돼야 한다.** 파형은 거들 뿐이다.
    if (!AudioContextClass) return;
    try {
      const context = new AudioContextClass();
      audioContextRef.current = context;
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.72;
      context.createMediaStreamSource(stream).connect(analyser);
      const spectrum = new Uint8Array(analyser.frequencyBinCount);
      const bandSize = Math.floor(spectrum.length / 12);
      let lastPainted = 0;
      const paint = (): void => {
        analyserFrameRef.current = window.requestAnimationFrame(paint);
        // 60fps 로 state 를 갈아치울 이유가 없다 — 눈에는 30fps 로 충분하다.
        const now = performance.now();
        if (now - lastPainted < 33) return;
        lastPainted = now;
        analyser.getByteFrequencyData(spectrum);
        const next: number[] = [];
        for (let band = 0; band < 12; band += 1) {
          let sum = 0;
          for (let i = band * bandSize; i < (band + 1) * bandSize; i += 1) sum += spectrum[i] ?? 0;
          // 0~255 를 0~1 로. 조용하면 0 에 가까워 막대가 바닥에서 «멈춘다».
          next.push(Math.min(1, (sum / bandSize) / 190));
        }
        setLevels(next);
      };
      paint();
    } catch {
      // 분석에 실패해도 녹음은 그대로 간다.
      stopLevelMeter();
    }
  };

  const startRecording = async (): Promise<void> => {
    setError(null);
    setLimitNotice(null);
    setPermissionDenied(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      startLevelMeter(stream);
      const preferred = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus"]
        .find((mime) => MediaRecorder.isTypeSupported(mime));
      const recorder = preferred ? new MediaRecorder(stream, { mimeType: preferred }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        // 🩸 2026-10-08: 1~3초 녹음이 올라가 음악 AI 가 거절했다. 녹음 webm 은 길이를 헤더에
        //    안 적으므로(아래 checkDuration 이 못 읽는다) 멈춘 시각으로 잰다. 서버도 다시 잰다.
        const recordedSeconds = ((stoppedAtRef.current ?? Date.now()) - startedAtRef.current) / 1_000;
        if (recordedSeconds < HUMMING_MIN_SECONDS) {
          setRecordingBlob(null);
          setRecordingUrl((current) => {
            if (current) URL.revokeObjectURL(current);
            return null;
          });
          setError(HUMMING_TOO_SHORT_MESSAGE);
        } else {
          setRecordingBlob(blob);
          setRecordingUrl((current) => {
            if (current) URL.revokeObjectURL(current);
            return URL.createObjectURL(blob);
          });
        }
        setRecording(false);
        stopLevelMeter();
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };
      startedAtRef.current = Date.now();
      stoppedAtRef.current = null;
      setSeconds(0);
      setRecordingBlob(null);
      setSource("recording");
      setFileName(null);
      recorder.start(500);
      setRecording(true);
    } catch {
      stopLevelMeter();
      setPermissionDenied(true);
    }
  };

  const stopRecording = (): void => {
    // 길이는 «멈춘 순간» 으로 잰다 — onstop 은 느린 기기에서 늦게 와 길이를 부풀린다.
    if (recorderRef.current?.state === "recording") {
      stoppedAtRef.current = Date.now();
      recorderRef.current.stop();
    }
  };

  /**
   * 파일에서 고르기 — 마이크가 안 될 때만 열리는 문이 아니라 **늘 열려 있는 문**이다.
   * 교실에서 마이크를 못 쓰는 사정(소음·기기·권한)은 실패가 아니라 흔한 상황이다.
   */
  const chooseFile = (file: File): void => {
    setError(null);
    setLimitNotice(null);
    // 보내기 전에 그 자리에서 걸러 준다 — 서버까지 다녀와서 알려 줄 이유가 없다.
    const rejection = checkHummingFile(file);
    if (rejection) {
      setRecordingBlob(null);
      setRecordingUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      setFileName(null);
      setError(rejection);
      return;
    }
    setSource("file");
    setFileName(file.name);
    setSeconds(0);
    setRecordingBlob(file);
    setRecordingUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return URL.createObjectURL(file);
    });
  };

  /**
   * 고른 파일이 60초를 넘는지는 브라우저가 길이를 읽은 뒤에야 알 수 있다.
   * 서버도 막지만(422), 올리기 전에 말해 주는 편이 낫다.
   */
  const checkDuration = (durationSeconds: number): void => {
    if (!Number.isFinite(durationSeconds)) return;
    const rounded = Math.round(durationSeconds);
    if (source === "file") setSeconds(Math.min(HUMMING_MAX_SECONDS, rounded));
    if (rounded > HUMMING_MAX_SECONDS) {
      setError(`${HUMMING_MAX_SECONDS}초까지 올릴 수 있어요. 더 짧은 소리를 골라 주세요`);
      setRecordingBlob(null);
    } else if (durationSeconds < HUMMING_MIN_SECONDS) {
      setError(HUMMING_TOO_SHORT_MESSAGE);
      setRecordingBlob(null);
    }
  };

  const chooseLyrics = async (): Promise<void> => {
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "lyrics", mode: "lyrics" }),
      });
      navigate(pathForStep(workId, "lyrics"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "가사 화면으로 이동하지 못했습니다.");
    }
  };

  const useHumming = async (): Promise<void> => {
    if (!recordingBlob) return;
    setUploading(true);
    setError(null);
    const form = new FormData();
    // 서버는 확장자를 믿지 않고 내용으로 판별하지만, 변환 단계가 확장자를 참고하므로
    // 고른 파일은 **그 이름 그대로** 보낸다(녹음은 종전대로 webm/ogg).
    const extension = recordingBlob.type.includes("ogg") ? "ogg" : "webm";
    form.append("audio", recordingBlob, fileName ?? `humming.${extension}`);
    try {
      await apiRequest<{ asset_id: string; duration_seconds: number }>(
        `/api/works/${encodeURIComponent(workId)}/humming`,
        { method: "POST", body: form },
      );
      const saved = await apiRequest<AutosaveResponse>(
        `/api/works/${encodeURIComponent(workId)}/autosave`,
        {
          method: "POST",
          body: JSON.stringify({ step: "style", mode: "humming", lyrics }),
        },
      );
      publishPendingGifts(saved.pending_gifts);
      navigate(pathForStep(workId, "style"));
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "RECORDING_TOO_SHORT") {
        setError(HUMMING_TOO_SHORT_MESSAGE);
        setRecordingBlob(null);
      } else if (caught instanceof ApiError) {
        // 🔑 파일을 골라 놓고 「다시 녹음해 주세요」라고 하면 무엇을 하라는 건지 알 수 없다.
        const fromFile = source === "file";
        setError(
          caught.status === 413
            ? fromFile
              ? "파일이 너무 커요. 10MB 이하로 올려 주세요"
              : "녹음이 너무 길거나 커요. 다시 녹음해 주세요"
            : caught.status === 422
              ? fromFile
                ? "이 파일은 소리를 읽지 못했어요. 다른 파일을 골라 주세요"
                : "녹음을 읽지 못했어요. 다시 녹음해 주세요"
              : caught.status === 429
                ? "허밍 올리기는 수업에서 5번까지예요"
                : "네트워크 연결을 확인하고 다시 시도해 주세요",
        );
      } else {
        setError("네트워크 연결을 확인하고 다시 시도해 주세요");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <StudioShell currentStep={2} remainingSlots={query.data.remaining_slots}>
      <div className="humming-page-content">
        <section className="humming-recording-panel">
          <div className="humming-heading">
            <h1 tabIndex={-1}>멜로디를 허밍해 주세요</h1>
            <p>입을 다문 채 “음~” 하고 부르거나, 편한 소리로 멜로디를 들려주세요.</p>
          </div>

          {permissionDenied ? (
            <div className="humming-permission-card">
              <strong>마이크 권한이 필요해요</strong>
              <p>브라우저 주소창의 마이크 설정을 허용하거나, 이미 있는 소리 파일을 올려 주세요.</p>
              <div className="humming-actions">
                <button className="button-primary" type="button" onClick={() => void startRecording()}>다시 권한 요청</button>
                <button
                  className="button-secondary"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  파일에서 고르기
                </button>
                <button className="button-secondary" type="button" onClick={() => void chooseLyrics()}>가사로 만들기</button>
              </div>
            </div>
          ) : (
            <div className={`humming-recorder${recording ? " is-recording" : ""}`}>
              <div className="humming-counter" aria-live="polite">{seconds}<small>/ 60초</small></div>
              {recording ? (
                <div className="wave-bars humming-wave" aria-hidden="true">
                  {/* 높이가 «들어오는 소리» 에서 온다 — 조용하면 6px 바닥에서 멈춘다. */}
                  {levels.map((level, index) => (
                    <i
                      key={index}
                      style={{ "--wave-height": `${Math.round(6 + level * 58)}px` } as React.CSSProperties}
                    />
                  ))}
                </div>
              ) : null}
              {!recordingBlob && !recording ? (
                <div className="humming-start-actions">
                  <button className="record-button" type="button" onClick={() => void startRecording()}>
                    <span aria-hidden="true">●</span> 녹음 시작
                  </button>
                  {/* (가) 늘 열린 문 — 마이크 실패를 기다리지 않는다. */}
                  <button
                    className="button-secondary"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    파일에서 고르기
                  </button>
                  <p className="field-guidance">{HUMMING_FILE_HINT}</p>
                </div>
              ) : null}
              {recording ? (
                <button className="record-button stop" type="button" onClick={stopRecording}>■ 녹음 멈추기</button>
              ) : null}
              {recordingUrl && !recording ? (
                <div className="humming-review">
                  <strong>
                    {source === "file" ? "고른 소리를 확인해 보세요" : "녹음을 확인해 보세요"}
                  </strong>
                  {source === "file" && fileName ? (
                    <p className="field-guidance">{fileName}</p>
                  ) : null}
                  <audio
                    controls
                    src={recordingUrl}
                    preload="metadata"
                    onLoadedMetadata={(event) => checkDuration(event.currentTarget.duration)}
                  />
                  <button
                    className="button-secondary"
                    type="button"
                    disabled={uploading}
                    onClick={() => {
                      setRecordingBlob(null);
                      setRecordingUrl(null);
                      setFileName(null);
                      setSeconds(0);
                      setLimitNotice(null);
                      setError(null);
                    }}
                  >
                    {source === "file" ? "다른 파일 고르기" : "다시 녹음"}
                  </button>
                </div>
              ) : null}
              {limitNotice ? <p className="field-guidance">{limitNotice}</p> : null}
            </div>
          )}

          <input
            ref={fileInputRef}
            className="sr-only"
            type="file"
            accept={HUMMING_FILE_ACCEPT}
            onChange={(event) => {
              const picked = event.target.files?.[0];
              if (picked) chooseFile(picked);
              // 같은 파일을 다시 고를 수 있어야 한다 — 값을 비우지 않으면 change 가 안 난다.
              event.target.value = "";
            }}
          />
        </section>

        <section className="humming-lyrics-panel">
          <label className="humming-lyrics">
            <span>가사도 함께 넣을까요? <small>선택</small></span>
            <p>가사를 넣으면 내 멜로디에 맞춰 불러요. 비워 두면 허밍 느낌의 연주곡이 돼요.</p>
            <textarea
              value={lyrics}
              maxLength={5_000}
              onChange={(event) => setLyrics(event.target.value)}
              placeholder="가사가 없다면 비워 두어도 괜찮아요"
            />
          </label>
          <p className="humming-privacy-note">올린 소리는 노래를 만드는 데만 쓰고, 수업이 끝나면 지워요.</p>
          {error ? (
            <InlineError>
              {error}{" "}
              {recordingBlob
                ? source === "file" ? "고른 파일은 그대로 있어요." : "녹음은 그대로 보관되어 있어요."
                : ""}
            </InlineError>
          ) : null}
          <div className="humming-footer-actions">
            <button className="button-ghost" type="button" onClick={() => navigate(pathForStep(workId, "mode"))}>이전</button>
            <button
              className="button-primary"
              type="button"
              disabled={!recordingBlob || recording || uploading}
              onClick={() => void useHumming()}
            >
              {uploading ? <InlineSpinner label="허밍 올리는 중..." /> : "이 허밍 사용"}
            </button>
          </div>
        </section>
      </div>
    </StudioShell>
  );
}
